import React, { useState, useEffect } from 'react'
import { API_BASE_URL } from '../../config/api'
import { authFetch } from '../../utils/authFetch'
import {
  Save,
  Upload,
  Key,
  Database,
  Bell,
  Shield,
  Palette,
  Globe,
  Mail,
  Phone,
  MapPin,
  CreditCard,
  Users,
  Settings as SettingsIcon,
  MessageCircle,
  Plus,
  Trash2
} from 'lucide-react'

interface SettingsProps {
  tenantId: string
}

interface SalesCard {
  title: string
  description: string
  icon: string
  color: string
  search_query: string
}

interface WidgetConfig {
  operator_type: 'hotel' | 'tour' | 'activity' | 'safari'
  widget_header: string
  widget_subtitle: string
  sales_cards: SalesCard[]
}

interface TenantSettings {
  company: {
    name: string
    email: string
    phone: string
    address: string
    website: string
    logo_url: string
  }
  branding: {
    primary_color: string
    secondary_color: string
    font_family: string
    custom_css: string
  }
  integrations: {
    stripe_publishable_key: string
    stripe_secret_key: string
    openai_api_key: string
    pinecone_api_key: string
    google_maps_key: string
  }
  notifications: {
    email_notifications: boolean
    sms_notifications: boolean
    booking_confirmations: boolean
    payment_alerts: boolean
    low_inventory_alerts: boolean
  }
  features: {
    vector_search: boolean
    ai_recommendations: boolean
    multi_language: boolean
    auto_indexing: boolean
    advanced_analytics: boolean
  }
  widget_config: WidgetConfig
}

const Settings: React.FC<SettingsProps> = ({ tenantId }) => {
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState<'company' | 'branding' | 'integrations' | 'notifications' | 'features' | 'widget'>('company')
  const [settings, setSettings] = useState<TenantSettings>({
    company: {
      name: '',
      email: '',
      phone: '',
      address: '',
      website: '',
      logo_url: ''
    },
    branding: {
      primary_color: '#7c3aed',
      secondary_color: '#06b6d4',
      font_family: 'Inter',
      custom_css: ''
    },
    integrations: {
      stripe_publishable_key: '',
      stripe_secret_key: '',
      openai_api_key: '',
      pinecone_api_key: '',
      google_maps_key: ''
    },
    notifications: {
      email_notifications: true,
      sms_notifications: false,
      booking_confirmations: true,
      payment_alerts: true,
      low_inventory_alerts: true
    },
    features: {
      vector_search: true,
      ai_recommendations: true,
      multi_language: false,
      auto_indexing: true,
      advanced_analytics: false
    },
    widget_config: {
      operator_type: 'safari',
      widget_header: 'Plan Your Safari Adventure',
      widget_subtitle: 'Ask me anything about Safari Bookings or describe your dream safari experience',
      sales_cards: [
        {
          title: 'Best Time to Visit',
          description: 'When should I plan my safari?',
          icon: '📅',
          color: 'bg-blue-100 text-blue-600',
          search_query: 'best time to visit'
        },
        {
          title: 'Group Packages',
          description: 'Safari options for families',
          icon: '👥',
          color: 'bg-green-100 text-green-600',
          search_query: 'group packages'
        },
        {
          title: 'Luxury Experiences',
          description: 'Premium safari packages',
          icon: '🏆',
          color: 'bg-purple-100 text-purple-600',
          search_query: 'luxury experiences'
        }
      ]
    }
  })

  useEffect(() => {
    fetchSettings()
  }, [tenantId])

  const fetchSettings = async () => {
    try {
      const response = await authFetch(`${API_BASE_URL}/api/admin/settings?tenant_id=${tenantId}`)
      const data = await response.json()
      if (data.success) {
        setSettings(data.settings)
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error)
    }
  }

  const saveSettings = async () => {
    setLoading(true)
    try {
      // Save general settings (without widget_config)
      const { widget_config, ...generalSettings } = settings
      const generalResponse = await authFetch(`${API_BASE_URL}/api/admin/settings?tenant_id=${tenantId}`, {
        method: 'PUT',
        body: JSON.stringify(generalSettings)
      })

      // Try to save widget configuration separately
      try {
        await authFetch(`${API_BASE_URL}/api/admin/widget-config?tenant_id=${tenantId}`, {
          method: 'PUT',
          body: JSON.stringify({ widget_config })
        })
        
        if (generalResponse.ok) {
          alert('Settings saved successfully!')
        }
      } catch (widgetError) {
        // Widget config save may fail if column doesn't exist yet
        if (generalResponse.ok) {
          alert('General settings saved successfully! Widget settings will be saved once database is updated.')
        }
      }
      
    } catch (error) {
      console.error('Failed to save settings:', error)
      alert('Failed to save settings. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const tabs = [
    { id: 'company', name: 'Company Info', icon: Users },
    { id: 'branding', name: 'Branding', icon: Palette },
    { id: 'integrations', name: 'Integrations', icon: Key },
    { id: 'notifications', name: 'Notifications', icon: Bell },
    { id: 'features', name: 'Features', icon: SettingsIcon },
    { id: 'widget', name: 'Widget Customization', icon: MessageCircle },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-600">Manage your account settings and preferences</p>
        </div>
        <button
          onClick={saveSettings}
          disabled={loading}
          className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50"
        >
          {loading ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          Save Changes
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Company Info Tab */}
          {activeTab === 'company' && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900">Company Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Name
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    value={settings.company.name}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      company: { ...prev.company, name: e.target.value }
                    }))}
                    placeholder="Your Company Name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="email"
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      value={settings.company.email}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        company: { ...prev.company, email: e.target.value }
                      }))}
                      placeholder="contact@company.com"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="tel"
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      value={settings.company.phone}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        company: { ...prev.company, phone: e.target.value }
                      }))}
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Website
                  </label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="url"
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      value={settings.company.website}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        company: { ...prev.company, website: e.target.value }
                      }))}
                      placeholder="https://www.company.com"
                    />
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-3 text-gray-400 w-4 h-4" />
                  <textarea
                    rows={3}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    value={settings.company.address}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      company: { ...prev.company, address: e.target.value }
                    }))}
                    placeholder="123 Main St, City, State 12345"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Branding Tab */}
          {activeTab === 'branding' && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900">Brand Customization</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Primary Color
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      className="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer"
                      value={settings.branding.primary_color}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        branding: { ...prev.branding, primary_color: e.target.value }
                      }))}
                    />
                    <input
                      type="text"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      value={settings.branding.primary_color}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        branding: { ...prev.branding, primary_color: e.target.value }
                      }))}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Secondary Color
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      className="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer"
                      value={settings.branding.secondary_color}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        branding: { ...prev.branding, secondary_color: e.target.value }
                      }))}
                    />
                    <input
                      type="text"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      value={settings.branding.secondary_color}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        branding: { ...prev.branding, secondary_color: e.target.value }
                      }))}
                    />
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Font Family
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  value={settings.branding.font_family}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    branding: { ...prev.branding, font_family: e.target.value }
                  }))}
                >
                  <option value="Inter">Inter</option>
                  <option value="Roboto">Roboto</option>
                  <option value="Open Sans">Open Sans</option>
                  <option value="Lato">Lato</option>
                  <option value="Montserrat">Montserrat</option>
                </select>
              </div>
            </div>
          )}

          {/* Integrations Tab */}
          {activeTab === 'integrations' && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900">API Integrations</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    OpenAI API Key
                  </label>
                  <input
                    type="password"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    value={settings.integrations.openai_api_key}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      integrations: { ...prev.integrations, openai_api_key: e.target.value }
                    }))}
                    placeholder="sk-..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Pinecone API Key
                  </label>
                  <input
                    type="password"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    value={settings.integrations.pinecone_api_key}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      integrations: { ...prev.integrations, pinecone_api_key: e.target.value }
                    }))}
                    placeholder="Pinecone API key"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Google Maps API Key
                  </label>
                  <input
                    type="password"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    value={settings.integrations.google_maps_key}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      integrations: { ...prev.integrations, google_maps_key: e.target.value }
                    }))}
                    placeholder="Google Maps API key"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900">Notification Preferences</h3>
              <div className="space-y-4">
                {Object.entries(settings.notifications).map(([key, value]) => (
                  <label key={key} className="flex items-center">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      checked={value}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        notifications: { ...prev.notifications, [key]: e.target.checked }
                      }))}
                    />
                    <span className="ml-3 text-sm text-gray-700">
                      {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Features Tab */}
          {activeTab === 'features' && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900">Feature Settings</h3>
              <div className="space-y-4">
                {Object.entries(settings.features).map(([key, value]) => (
                  <label key={key} className="flex items-center">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      checked={value}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        features: { ...prev.features, [key]: e.target.checked }
                      }))}
                    />
                    <span className="ml-3 text-sm text-gray-700">
                      {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Widget Customization Tab */}
          {activeTab === 'widget' && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900">Widget Customization</h3>
              
              {/* Operator Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Operator Type
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { value: 'hotel', label: 'Hotel', icon: '🏨' },
                    { value: 'tour', label: 'Tour Operator', icon: '🗺️' },
                    { value: 'activity', label: 'Activity Provider', icon: '🎯' },
                    { value: 'safari', label: 'Safari Lodge', icon: '🦁' }
                  ].map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setSettings(prev => ({
                        ...prev,
                        widget_config: { ...prev.widget_config, operator_type: type.value as any }
                      }))}
                      className={`p-4 border-2 rounded-lg text-center transition-all ${
                        settings.widget_config.operator_type === type.value
                          ? 'border-purple-500 bg-purple-50 text-purple-700'
                          : 'border-gray-200 hover:border-gray-300 text-gray-700'
                      }`}
                    >
                      <div className="text-2xl mb-2">{type.icon}</div>
                      <div className="text-sm font-medium">{type.label}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Widget Header */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Widget Header
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  value={settings.widget_config.widget_header}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    widget_config: { ...prev.widget_config, widget_header: e.target.value }
                  }))}
                  placeholder="Plan Your Perfect Experience"
                />
              </div>

              {/* Widget Subtitle */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Widget Subtitle
                </label>
                <textarea
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  value={settings.widget_config.widget_subtitle}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    widget_config: { ...prev.widget_config, widget_subtitle: e.target.value }
                  }))}
                  placeholder="Ask me anything about our services or describe your dream experience"
                />
              </div>

              {/* Sales Cards Configuration */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Sales Cards (Max 3)
                  </label>
                  {settings.widget_config.sales_cards.length < 3 && (
                    <button
                      type="button"
                      onClick={() => setSettings(prev => ({
                        ...prev,
                        widget_config: {
                          ...prev.widget_config,
                          sales_cards: [...prev.widget_config.sales_cards, {
                            title: '',
                            description: '',
                            icon: '🔍',
                            color: 'bg-blue-100 text-blue-600',
                            search_query: ''
                          }]
                        }
                      }))}
                      className="flex items-center gap-2 px-3 py-1 text-sm text-purple-600 hover:text-purple-700 border border-purple-200 hover:border-purple-300 rounded-lg"
                    >
                      <Plus className="w-4 h-4" />
                      Add Card
                    </button>
                  )}
                </div>

                <div className="space-y-4">
                  {settings.widget_config.sales_cards.map((card, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-gray-700">Card {index + 1}</span>
                        <button
                          type="button"
                          onClick={() => setSettings(prev => ({
                            ...prev,
                            widget_config: {
                              ...prev.widget_config,
                              sales_cards: prev.widget_config.sales_cards.filter((_, i) => i !== index)
                            }
                          }))}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Title</label>
                          <input
                            type="text"
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-purple-500"
                            value={card.title}
                            onChange={(e) => setSettings(prev => ({
                              ...prev,
                              widget_config: {
                                ...prev.widget_config,
                                sales_cards: prev.widget_config.sales_cards.map((c, i) => 
                                  i === index ? { ...c, title: e.target.value } : c
                                )
                              }
                            }))}
                            placeholder="Best Time to Visit"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                          <input
                            type="text"
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-purple-500"
                            value={card.description}
                            onChange={(e) => setSettings(prev => ({
                              ...prev,
                              widget_config: {
                                ...prev.widget_config,
                                sales_cards: prev.widget_config.sales_cards.map((c, i) => 
                                  i === index ? { ...c, description: e.target.value } : c
                                )
                              }
                            }))}
                            placeholder="When should I plan my trip?"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Icon (Emoji)</label>
                          <input
                            type="text"
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-purple-500"
                            value={card.icon}
                            onChange={(e) => setSettings(prev => ({
                              ...prev,
                              widget_config: {
                                ...prev.widget_config,
                                sales_cards: prev.widget_config.sales_cards.map((c, i) => 
                                  i === index ? { ...c, icon: e.target.value } : c
                                )
                              }
                            }))}
                            placeholder="📅"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Color Theme</label>
                          <select
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-purple-500"
                            value={card.color}
                            onChange={(e) => setSettings(prev => ({
                              ...prev,
                              widget_config: {
                                ...prev.widget_config,
                                sales_cards: prev.widget_config.sales_cards.map((c, i) => 
                                  i === index ? { ...c, color: e.target.value } : c
                                )
                              }
                            }))}
                          >
                            <option value="bg-blue-100 text-blue-600">Blue</option>
                            <option value="bg-green-100 text-green-600">Green</option>
                            <option value="bg-purple-100 text-purple-600">Purple</option>
                            <option value="bg-orange-100 text-orange-600">Orange</option>
                            <option value="bg-pink-100 text-pink-600">Pink</option>
                            <option value="bg-indigo-100 text-indigo-600">Indigo</option>
                          </select>
                        </div>

                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Search Query</label>
                          <input
                            type="text"
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-purple-500"
                            value={card.search_query}
                            onChange={(e) => setSettings(prev => ({
                              ...prev,
                              widget_config: {
                                ...prev.widget_config,
                                sales_cards: prev.widget_config.sales_cards.map((c, i) => 
                                  i === index ? { ...c, search_query: e.target.value } : c
                                )
                              }
                            }))}
                            placeholder="best time to visit"
                          />
                          <p className="text-xs text-gray-500 mt-1">This query will be sent when users click this card</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Widget Preview */}
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 mb-4">Widget Preview</h4>
                <div className="bg-white rounded-lg shadow-sm border p-6 max-w-md mx-auto">
                  <div className="text-center mb-4">
                    <div className="w-12 h-12 bg-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-3">
                      <MessageCircle className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">
                      {settings.widget_config.widget_header || 'Widget Header'}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {settings.widget_config.widget_subtitle || 'Widget subtitle text'}
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-1 gap-3">
                    {settings.widget_config.sales_cards.slice(0, 3).map((card, index) => (
                      <div key={index} className={`p-3 rounded-lg ${card.color || 'bg-gray-100'}`}>
                        <div className="flex items-center gap-3">
                          <span className="text-lg">{card.icon || '❓'}</span>
                          <div>
                            <div className="font-medium text-sm">{card.title || 'Card Title'}</div>
                            <div className="text-xs opacity-80">{card.description || 'Card description'}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Settings